/*-----------------------------------------------------------------------------
|> TVGuide Component
|------------------------------------------------------------------------------
|
| Render a OnTV tv grid for a specific lineup
|
-----------------------------------------------------------------------------*/
<template>
    <div v-if="api.errors.message" class="tv-guide container-fluid">
        <div class="guide-progress-indicator">
            <h2 v-if="isNetworkError" class="pt-5">
                Network Error. Check your internet connection.
            </h2>
            <h2 v-else class="pt-5">
                You are not authorized to view this content.
            </h2>
        </div>
    </div>
    <div v-else ref="guideContainer" class="tv-guide container-fluid">
        <div
            v-if="channels.length === 0"
            class="guide-progress-indicator"
        >
            <font-awesome-icon icon="compact-disc" spin />
        </div>

        <div v-if="currentChannel" class="guide-header row">
            <div class="guide-logo col">
                <img v-if="currentChannel" :src="coverLogo" alt="Channel logo of current channel being played">
            </div>

            <div class="title col header-links" v-if="auth">
                <span>
                    <a :href="dashboardUrl">{{ DashboardText }}</a>
                </span>
            </div>

            <div class="col" v-else>
                <div class="align-right">
                    <div class="navigation">
                        <a :href="loginUrl" class="btn-outline btn-outline-secondary btn-lg" role="button" aria-pressed="true">Sign in</a>
                    </div>
                </div>
            </div>
        </div>

        <div ref="guideContent" class="guide-content row">
            <video-player
                v-if="playerHeight"
                :height="playerHeight"
                :streaming-url="currentChannel.streaming_url"
                :channel-name="currentChannel.name"
                :muted="playerMuted"
                @mute-toggle="(muted) => playerMuted = muted"
            />
        </div>

        <div v-if="currentChannel" class="guide-schedule row">
            <span
                class="schedule-slide-nav schedule-slide-left swiper-button-prev"
                @click="scrollSchedule(SCROLL_DIRECTION_RIGHT)"
            />

            <span
                class="schedule-slide-nav schedule-slide-right swiper-button-next"
                @click="scrollSchedule(SCROLL_DIRECTION_LEFT)"
            />

            <schedule-timeline
                :current-time="currentTime"
                :program-title="currentAiring.program.title"
                @swiped="swipeSchedule"
            />

            <channels
                v-if="channels.length > 0"
                :data="channels"
                :start-time="currentTime.toISOString()"
                :swipe-handler="swipeSchedule"
            />
        </div>
    </div>
</template>

<script>
import dayjs from 'dayjs';

import { library } from '@fortawesome/fontawesome-svg-core';
import {  faCompactDisc } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { getLineup } from '@api/iptv';

import Channels from '@components/iptv/ChannelList.vue';
import ScheduleTimeline from '@components/iptv/ScheduleTimeline.vue';
import VideoPlayer from '@components/iptv/VideoPlayer';

library.add(faCompactDisc);

const SCROLL_DIRECTION_LEFT = 2;
const SCROLL_DIRECTION_RIGHT = 4;

export default {
    name: 'IPTV',
    components: {
        VideoPlayer,
        Channels,
        ScheduleTimeline,
        FontAwesomeIcon,
    },
    props: {
        coverLogo: { type: String, default: '' },
        baseFontSize: { type: Number, required: false, default: 16 },
        imageSize: { type: String, default: 'Sm' },
        lineup: { type: String, required: false, default: '' },
        resolution: { type: String, required: false, default: '' },
        screenPadding: { type: Number, required: false, default: 0 },
        scrollOffset: { type: Number, required: false, default: 0 },
        startTime: { type: String, required: false, default: dayjs() },
        scheduleLength: { type: Number, required: false, default: 3 },
        token: { type: String, required: false, default: '' },
        auth: { type: Boolean, required: false, default: false },
        loginUrl: { type: String, required: false, default: '' },
        registerUrl: { type: String, required: false, default: '' },
        dashboardUrl: { type: String, required: false, default: '' },
        DashboardText: { type: String, required: false, default: '' },
    },
    data() {
        return {
            SCROLL_DIRECTION_LEFT: SCROLL_DIRECTION_LEFT,
            SCROLL_DIRECTION_RIGHT: SCROLL_DIRECTION_RIGHT,
            /* Available TV guide resolutions with size, ratio mapping */
            guideResolutions: {
                sd: { width: 720, height: 540, ratio: '4:3', size: 'Md' },
                hd: { width: 1280, height: 720, ratio: '16:9', size: 'Md' },
                fhd: { width: 1920, height: 1080, ratio: '16:9', size: 'Lg' },
                uhd: { width: 3840, height: 2160, ratio: '16:9', size: 'Lg' },
            },
            channels: [],
            currentChannel: null,
            currentTime: this.$root.currentTime,
            playerHeight: 0,
            playerMuted: true,
            refreshInterval: 60,
            refreshIntervalId: null,
            api: { busy: false, errors: {}, progress: 0 }, // API requests status
        }
    },
    computed: {
        /**
         * Returns the first show for the current/active channel which
         * `endTime` hasn't pass the client's specified `startTime`(or current time).
         */
        currentAiring() {
            return this.currentChannel.airings
                .find((airing) => {
                    return dayjs(airing.endTime)
                        .diff(dayjs(this.currentTime), 'minutes') >= 0;
                });
        },
        currentChannelLogo() {
            return this.currentChannel.preferredImage.url || this.$root.defaultLogo;
        },
        /**
         * Returns an array of datetime(dayjs) instances one hour apart from each
         *  other. Starting at the `currentAiring` start time.
         */
        scheduleTimebar() {
            let nextTimeSegment = this.currentTime.startOf('hour');
            let timebar = [ nextTimeSegment ];

            for (let i = 1; i < this.$root.timebarItemCount; i++) {
                nextTimeSegment = nextTimeSegment
                    .add(this.$root.timebarItemLength, 'minutes');
                timebar.push(nextTimeSegment);
            }

            return timebar;
        },
        /**
         * Horizontal pixel dimension(width) of individual timebar items/segments
         */
        timebarItemWidth() {
            return 150;
            // return (window.visualViewport.width - this.$root.channelColumnWidth) / this.$root.timebarItemCount;
        },
    },
    watch: {
        channels() {
            if (this.channels.length === 0) return;

            this.updateGuide();
        },
        baseFontSize() {
            document.body.style.fontSize = `${this.baseFontSize}px`;
        },
    },
    created() {
        this.getChannels();

        this.refreshIntervalId = setInterval(() => {
            this.getChannels();

        }, this.refreshInterval * 1000);

        document.body.style.padding = `${this.screenPadding}%`;

        this.$root.$on('playChannel', (channelId) => {
            this.setCurrentChannel(channelId);

            const previousChannel = document.querySelector('.channel-current');

            if (previousChannel) {
                previousChannel.classList.remove('channel-current');
            }

            const currentChannel = document.querySelector(`.channel-${channelId}`);
            currentChannel.classList.add('channel-current');
        })
    },
    beforeUnmount() {
        clearInterval(this.refreshIntervalId);
    },
    mounted() {
        window.addEventListener('resize', () => {
            this.updateHeight();
        });
    },
    methods: {
        isNetworkError() {
            if (this.api.errors.message && this.api.errors.message === 'Network error') {
                return true;
            }
            return false;
        },
        datetime: dayjs,
        getChannels() {
            getLineup(this.resolution, this.lineup, this.api, this.token)
                .then((response) => {
                    this.channels = response;
                    if (this.channels.length > 0) {
                        this.channels.forEach((channel, index) => {
                            if (channel.streaming_url_override) {
                                this.channels[index].streaming_url = channel.streaming_url_override;
                                return;
                            }
                            if (channel.streaming_server_url && channel.stream_identifier && channel.stream_token) {
                                const ago = 10;
                                const streamUrl = `${channel.streaming_server_url}${channel.stream_identifier}/rewind-${ago}.fmp4.m3u8?token=${channel.stream_token}`;
                                this.channels[index].streaming_url = streamUrl;
                            }
                        });
                    }
                    if (this.api.errors && this.channels.length > 0) {
                        this.api.errors = {};
                    }
                });
        },
        scrollSchedule(direction, distance = 150) {
            const timeline = document.querySelector('.schedule-timeline');
            const marginLeft = timeline.style.marginLeft || '0px';

            /* prevent left scrolling if left edge is reached */
            if (direction === SCROLL_DIRECTION_RIGHT && (marginLeft === '0px' || marginLeft === 'calc(0px)')) {
                return;
            }

            /* prevent scrolling right when end of schedule is reached */
            if (direction === SCROLL_DIRECTION_LEFT) {
                const scheduleTimebar = document.querySelector('.guide-schedule-timeline');

                if (scheduleTimebar.offsetWidth === scheduleTimebar.scrollWidth) {
                    return;
                }
            }

            /* horizontal scrolling distance plus direction(sign) */
            const deltaX = direction === SCROLL_DIRECTION_RIGHT
                ? `+ ${Math.round(distance)}`
                : `- ${Math.round(distance)}`;

            timeline.style.marginLeft = `calc(${marginLeft} ${deltaX}px)`;

            const channelAirings = document.querySelectorAll('.channel-airings');

            channelAirings.forEach((airings) => {
                const marginLeft = airings.style.marginLeft || '0px';

                airings.style.marginLeft = `calc(${marginLeft} ${deltaX}px)`;
            });

            const timelineCurrent = document.querySelector('.schedule-timeline-current');
            const liveAirings = document.querySelectorAll('.live-airing');

            /* add/remove class that shrinks live show/airing and current timeline item on mobile  */
            if (Number(window.getComputedStyle(timeline).marginLeft.replace('px', '')) >= 0) {
                if (timelineCurrent.classList.contains('shrinked')) {
                    timelineCurrent.classList.remove('shrinked');
                }

                liveAirings.forEach((airing) => {
                    if (airing.classList.contains('shrinked')) {
                        airing.classList.remove('shrinked');
                    }
                });

            } else {
                if (!timelineCurrent.classList.contains('shrinked')) {
                    timelineCurrent.classList.add('shrinked');
                }

                liveAirings.forEach((airing) => {
                    if (!airing.classList.contains('shrinked')) {
                        airing.classList.add('shrinked');
                    }
                });
            }
        },
        swipeSchedule(swipe) {
            if (swipe.direction !== SCROLL_DIRECTION_LEFT && swipe.direction !== SCROLL_DIRECTION_RIGHT) {
                return;
            }

            // this.scrollSchedule(swipe.direction, swipe.distance);
            this.scrollSchedule(swipe.direction, 150);
        },
        /**
         * Sets guide cover details to top channel
         */
        setCurrentChannel(channelId = null) {
            if(channelId) {
                this.currentChannel = this.channels.find((channel) => {
                    return channel.channel === channelId;
                });
            } else {
                this.currentChannel = this.channels[0];
            }

            this.updateHeight();
        },
        /**
         * Reset/set animation and update guide details with current channel
         */
        updateGuide() {
            if (this.currentChannel == null) {
                this.setCurrentChannel();
            }

            this.currentTime = dayjs();
            this.$root.currentTime = dayjs();
        },
        updateHeight() {
            if (this.currentChannel) {
                const pixelPadding = window.getComputedStyle(document.body)
                    .getPropertyValue('padding')
                    .replace('px', '');

                this.$refs.guideContainer.style.height = `${document.body.offsetHeight - (Number(pixelPadding) * 2)}px`;
                this.playerHeight = this.$refs.guideContent.offsetHeight;
            }
        },
    },
}
</script>
